import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import { Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslatedStageName } from '../../../hooks/useTranslatedStageName';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../../theme/typography';
import { percentValue } from '../utils/metricDisplay';

export interface FunnelStage {
  id: string;
  name: string;
  // Leads currently sitting in the stage. Occupancy: a state, not a flow.
  count: number;
  // Conversion from the funnel's entry stage, computed by the API over the
  // leads that were EVER in each stage during the window. Null means the entry
  // stage population is empty, so no share of it exists. This component renders
  // what it is given and never recomputes: the previous version divided
  // occupancy by the first stage's occupancy, which is zero exactly when every
  // lead has advanced, and reported 100% for every row of the funnel.
  percentage: number | null;
  color?: string;
}

interface ConversionFunnelProps {
  stages: FunnelStage[];
  loading?: boolean;
  showDropIndicators?: boolean;
}

const STAGE_LABEL_WIDTH = 96;
const STAGE_CONVERSION_WIDTH = 42;
const STAGE_BAR_HEIGHT = 34;

// No leads in the stage means no bar. The 5% floor keeps a small-but-real
// occupancy visible; applying it to zero — as the old 14% floor did — drew
// "a few leads" for a stage holding none, and pinned every bar to the floor
// whenever the first stage was empty.
//
// The scale is the largest stage in the funnel, not the first one: the first
// stage is routinely empty, which left nothing to divide by.
export function resolveFunnelBarWidth(count: number, maxCount: number): number {
  const percentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return percentage > 0 ? Math.max(percentage, 5) : 0;
}

function withAlpha(color: string, alpha: number): string {
  if (color.startsWith('hsl(')) {
    return color.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
  }
  return color;
}

function FunnelBar({
  stage,
  index,
  maxCount,
}: Readonly<{
  stage: FunnelStage;
  index: number;
  maxCount: number;
}>) {
  const translateStageName = useTranslatedStageName();
  const widthAnim = useRef(new Animated.Value(0)).current;
  const fillPercentage = resolveFunnelBarWidth(stage.count, maxCount);
  const stageColor = stage.color ?? colors.primary[400];

  useEffect(() => {
    Animated.timing(widthAnim, {
      toValue: fillPercentage,
      duration: 750,
      delay: index * 90,
      useNativeDriver: false,
    }).start();
  }, [fillPercentage, index, widthAnim]);

  return (
    <HStack spacing="md" align="center">
      <Text
        variant="bodySmall"
        color={colors.text.secondary}
        numberOfLines={1}
        style={{ width: STAGE_LABEL_WIDTH }}
      >
        {translateStageName(stage.name)}
      </Text>
      <Box
        backgroundColor={colors.surface.row}
        borderRadius="sm"
        overflow="hidden"
        style={{ flex: 1, height: STAGE_BAR_HEIGHT, justifyContent: 'center' }}
      >
        <Animated.View
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            borderRadius: tokens.radius.sm,
            overflow: 'hidden',
            width: widthAnim.interpolate({
              inputRange: [0, 100],
              outputRange: ['0%', '100%'],
            }),
          }}
        >
          <LinearGradient
            colors={[stageColor, withAlpha(stageColor, 0.72)]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={{ flex: 1, borderRadius: tokens.radius.sm }}
          />
        </Animated.View>
        <Text
          color={colors.text.primary}
          style={{
            fontFamily: darkPrecisionFontFamilies.metricBold,
            fontSize: 13,
            fontWeight: '700',
            marginLeft: 12,
            textShadowColor: 'rgba(0,0,0,0.55)',
            textShadowOffset: { width: 0, height: 1 },
            textShadowRadius: 3,
          }}
        >
          {stage.count.toLocaleString()}
        </Text>
      </Box>
      <Text
        color={colors.text.secondary}
        style={{
          width: STAGE_CONVERSION_WIDTH,
          textAlign: 'right',
          fontFamily: darkPrecisionFontFamilies.metric,
          fontSize: 12,
        }}
      >
        {percentValue(stage.percentage, 0)}
      </Text>
    </HStack>
  );
}

export function ConversionFunnel({ stages, loading }: Readonly<ConversionFunnelProps>) {
  if (loading) {
    return (
      <VStack spacing="md">
        {[0, 1, 2, 3].map((i) => (
          <HStack key={i} spacing="md" align="center">
            <Box
              backgroundColor={colors.surface.row}
              borderRadius="sm"
              style={{ height: 14, width: STAGE_LABEL_WIDTH }}
            />
            <Box
              backgroundColor={colors.surface.row}
              borderRadius="sm"
              style={{ flex: 1, height: STAGE_BAR_HEIGHT }}
            />
            <Box
              backgroundColor={colors.surface.row}
              borderRadius="sm"
              style={{ height: 12, width: STAGE_CONVERSION_WIDTH }}
            />
          </HStack>
        ))}
      </VStack>
    );
  }

  const maxCount = Math.max(...stages.map((stage) => stage.count), 0);

  return (
    <VStack spacing="md">
      {stages.map((stage, index) => (
        <FunnelBar key={stage.id} stage={stage} index={index} maxCount={maxCount} />
      ))}
    </VStack>
  );
}
